import { Head, Link, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface AdminAccount {
  id: number
  name: string
  account_type: string
  logo_url: string | null
  members_count: number
  listings_count: number
  credit_balance: number
  subscription_plan_tier: string | null
  subscription_status: string | null
  subscription_plan_name: string | null
  has_stripe_subscription: boolean
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  search: string | null
  sort: string
  direction: string
  account_type: string | null
  accounts: AdminAccount[]
  pagination: PaginationMeta
}

const COLUMNS = [
  { key: "name", label: "Account", sortable: true },
  { key: "account_type", label: "Type", sortable: true },
  { key: "subscription", label: "Plan", sortable: false },
  { key: "members_count", label: "Members", sortable: true },
  { key: "listings_count", label: "Listings", sortable: false },
  { key: "credit_balance", label: "Credits", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
  { key: "actions", label: "", sortable: false },
] as const

const TYPE_FILTERS = [
  { value: "", label: "All types" },
  { value: "agency", label: "Agency" },
  { value: "individual", label: "Individual" },
] as const

export default function AdminAccountsIndex({ search, sort, direction, account_type, accounts, pagination }: Props) {
  const [searchValue, setSearchValue] = useState(search || "")
  const [deleteTarget, setDeleteTarget] = useState<AdminAccount | null>(null)

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (search) p.search = search
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      if (account_type) p.account_type = account_type
      const filtered = Object.entries(overrides).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== "")
      return { ...p, ...Object.fromEntries(filtered) }
    },
    [search, sort, direction, account_type],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/accounts", buildParams({ search: searchValue || undefined, page: undefined }), { preserveState: true })
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get("/admin/accounts", buildParams({ sort: column, direction: newDirection, page: undefined }), { preserveState: true })
  }

  function handleTypeFilter(value: string) {
    router.get("/admin/accounts", buildParams({ account_type: value || undefined, page: undefined }), { preserveState: true })
  }

  return (
    <AdminLayout>
      <Head title="Accounts | Admin" />
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">Accounts</h1>
            <p className="mt-1 text-sm text-cream/50">
              {pagination.count} {pagination.count === 1 ? "account" : "accounts"}
            </p>
          </div>

          <div className="flex gap-2">
            <Button size="sm" asChild>
              <Link href="/admin/accounts/new">New account</Link>
            </Button>

            <div className="flex gap-1 rounded-lg border border-white/10 bg-white/5 p-0.5">
              {TYPE_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => handleTypeFilter(filter.value)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    (account_type || "") === filter.value
                      ? "bg-white/10 text-cream"
                      : "text-cream/40 hover:text-cream/70"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
                <Input
                  placeholder="Search accounts..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
                />
              </div>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-cream/50 hover:text-cream"
                  onClick={() => {
                    setSearchValue("")
                    router.get("/admin/accounts", buildParams({ search: undefined }), { preserveState: true })
                  }}
                >
                  Clear
                </Button>
              )}
            </form>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                {COLUMNS.map((col) => (
                  <th key={col.key} className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">
                    {col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-cream/70 transition-colors"
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        <SortIcon active={sort === col.key} direction={sort === col.key ? direction : undefined} />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-cream/30">
                    {search ? `No accounts matching "${search}"` : "No accounts found"}
                  </td>
                </tr>
              )}
              {accounts.map((account) => {
                const initials = account.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)

                return (
                  <tr key={account.id} className="border-b border-white/[0.04] text-cream/70">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8 rounded-md">
                          <AvatarImage src={account.logo_url || undefined} className="object-contain" />
                          <AvatarFallback className="rounded-md bg-white/10 text-xs text-cream/60">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <Link href={`/admin/accounts/${account.id}`} className="font-medium text-cream hover:underline">{account.name}</Link>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge
                        variant="outline"
                        className={
                          account.account_type === "agency"
                            ? "border-blue-500/30 bg-blue-500/10 text-blue-400"
                            : "border-white/10 bg-white/5 text-cream/50"
                        }
                      >
                        {account.account_type}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <SubscriptionCell account={account} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-cream/50">
                      <div className="flex items-center gap-1.5">
                        <MembersIcon className="size-3.5 text-cream/30" />
                        {account.members_count}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-cream/50">{account.listings_count}</td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-cream/50">{account.credit_balance.toLocaleString()}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-cream/40">
                      {new Date(account.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="xs"
                          variant="ghost"
                          className="text-cream/40 hover:text-cream"
                          onClick={() => router.post(`/admin/accounts/${account.id}/impersonate`)}
                        >
                          Switch
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          className="text-red/40 hover:text-red"
                          onClick={() => setDeleteTarget(account)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          baseUrl="/admin/accounts"
          params={buildParams()}
          pagination={pagination}
        />
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
              This will permanently remove the account and all associated users, listings, videos, and credit history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleteTarget) {
                  router.delete(`/admin/accounts/${deleteTarget.id}`)
                  setDeleteTarget(null)
                }
              }}
            >
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  )
}

const PLAN_TIER_STYLES: Record<string, string> = {
  free:       "border-white/10 bg-white/5 text-cream/40",
  starter:    "border-blue-500/30 bg-blue-500/10 text-blue-400",
  pro:        "border-violet-500/30 bg-violet-500/10 text-violet-400",
  enterprise: "border-amber-500/30 bg-amber-500/10 text-amber-400",
}

const STATUS_DOT: Record<string, string> = {
  active:    "bg-emerald-400",
  past_due:  "bg-orange-400",
  cancelled: "bg-red-400",
  expired:   "bg-white/20",
}

function SubscriptionCell({ account }: { account: AdminAccount }) {
  const tier = account.subscription_plan_tier
  const status = account.subscription_status

  if (!tier || tier === "free") {
    return <span className="text-xs text-cream/25">—</span>
  }

  const label = account.subscription_plan_name ?? tier.charAt(0).toUpperCase() + tier.slice(1)
  const badgeClass = PLAN_TIER_STYLES[tier] ?? "border-white/10 bg-white/5 text-cream/50"
  const dotClass = status ? (STATUS_DOT[status] ?? "bg-white/20") : null

  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className={`text-[11px] ${badgeClass}`}>
        {label}
      </Badge>
      {dotClass && status !== "active" && (
        <span className="flex items-center gap-1 text-[10px] text-cream/40">
          <span className={`inline-block size-1.5 rounded-full ${dotClass}`} />
          {status?.replace("_", " ")}
        </span>
      )}
      {account.has_stripe_subscription && status === "active" && (
        <span className={`inline-block size-1.5 rounded-full ${STATUS_DOT.active}`} />
      )}
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}

function MembersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  )
}
